import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { CalendarIcon, ClockIcon, MapPinIcon, UserIcon, FileTextIcon, AlertTriangleIcon, CheckIcon, XIcon, EditIcon, DownloadIcon, PrinterIcon, PhoneIcon, MailIcon, InfoIcon, UsersIcon, ClipboardIcon } from 'lucide-react';
import { dataService } from '../../services/DataService';
import { ServiceStatus } from '../../models/TrainingService';
import { useAuth } from '../../contexts/AuthContext';
export function ServiceDetail() {
  const {
    serviceId
  } = useParams<{
    serviceId: string;
  }>();
  const navigate = useNavigate();
  const {
    role
  } = useAuth();
  const [trainingDetails, setTrainingDetails] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'documents' | 'notes'>('overview');
  const [loading, setLoading] = useState<boolean>(true);
  const [attendanceChecked, setAttendanceChecked] = useState<boolean>(false);
  useEffect(() => {
    if (serviceId) {
      setLoading(true);
      const details = dataService.getFullTrainingDetails(serviceId);
      setTrainingDetails(details);
      setLoading(false);
    }
  }, [serviceId]);
  if (loading) {
    return <div className="flex items-center justify-center h-64" data-id="element-3472">
        <div className="text-center" data-id="element-3473">
          <div className="w-12 h-12 border-4 border-[#B61C1C] border-t-transparent rounded-full animate-spin mx-auto" data-id="element-3474"></div>
          <p className="mt-4 text-gray-600" data-id="element-3475">Gegevens laden...</p>
        </div>
      </div>;
  }
  if (!trainingDetails || !trainingDetails.service) {
    return <div className="text-center py-12" data-id="element-3476">
        <AlertTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" data-id="element-3477" />
        <h2 className="text-xl font-medium text-gray-900 mb-2" data-id="element-3478">
          Training niet gevonden
        </h2>
        <p className="text-gray-600 mb-4" data-id="element-3479">
          De opgevraagde training kon niet worden gevonden.
        </p>
        <Button variant="outline" onClick={() => navigate(-1)} data-id="element-3480">
          Terug naar overzicht
        </Button>
      </div>;
  }
  const {
    service,
    project,
    instructor,
    rosterEntry
  } = trainingDetails;
  // Format date
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Get status badge
  const getStatusBadge = (status: ServiceStatus) => {
    switch (status) {
      case ServiceStatus.CONFIRMED:
        return <Badge variant="success" data-id="element-3481">Bevestigd</Badge>;
      case ServiceStatus.PLANNED:
        return <Badge variant="warning" data-id="element-3482">Gepland</Badge>;
      case ServiceStatus.IN_PROGRESS:
        return <Badge variant="info" data-id="element-3483">In uitvoering</Badge>;
      case ServiceStatus.COMPLETED:
        return <Badge variant="default" data-id="element-3484">Afgerond</Badge>;
      case ServiceStatus.CANCELLED:
        return <Badge variant="danger" data-id="element-3485">Geannuleerd</Badge>;
      default:
        return <Badge variant="default" data-id="element-3486">Onbekend</Badge>;
    }
  };
  // Mock participants data
  const participants = [{
    id: 1,
    name: 'Jan Janssen',
    email: 'j.janssen@bedrijf.nl',
    company: project?.clientName,
    present: true,
    notes: ''
  }, {
    id: 2,
    name: 'Piet Pietersen',
    email: 'p.pietersen@bedrijf.nl',
    company: project?.clientName,
    present: true,
    notes: ''
  }, {
    id: 3,
    name: 'Klaas Klaassen',
    email: 'k.klaassen@bedrijf.nl',
    company: project?.clientName,
    present: false,
    notes: 'Ziek gemeld'
  }, {
    id: 4,
    name: 'Henk Hendriks',
    email: 'h.hendriks@bedrijf.nl',
    company: project?.clientName,
    present: true,
    notes: ''
  }, {
    id: 5,
    name: 'Sjaak Sjaaksen',
    email: 's.sjaaksen@bedrijf.nl',
    company: project?.clientName,
    present: null,
    notes: ''
  }, {
    id: 6,
    name: 'Willem Willemsen',
    email: 'w.willemsen@bedrijf.nl',
    company: project?.clientName,
    present: null,
    notes: ''
  }];
  // Mock documents data
  const documents = [{
    id: 1,
    name: 'Trainingsprotocol.pdf',
    type: 'pdf',
    size: '1.2 MB',
    date: new Date(2023, 0, 15)
  }, {
    id: 2,
    name: 'Presentielijst.xlsx',
    type: 'excel',
    size: '245 KB',
    date: new Date(2023, 0, 15)
  }, {
    id: 3,
    name: 'Evaluatieformulier.pdf',
    type: 'pdf',
    size: '890 KB',
    date: new Date(2023, 0, 15)
  }, {
    id: 4,
    name: 'Veiligheidsinstructies.pdf',
    type: 'pdf',
    size: '1.5 MB',
    date: new Date(2023, 0, 15)
  }];
  // Render overview tab
  const renderOverviewTab = () => {
    return <div className="space-y-6" data-id="element-3487">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3488">
          {/* Training details */}
          <Card data-id="element-3489">
            <div className="p-4 border-b border-gray-200" data-id="element-3490">
              <h3 className="font-medium text-gray-900" data-id="element-3491">Training Details</h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-3492">
              <div className="space-y-3" data-id="element-3493">
                <div className="flex items-start" data-id="element-3494">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3495" />
                  <div data-id="element-3496">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3497">
                      Datum & Tijd
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3498">
                      {formatDate(service.startTime)} |{' '}
                      {formatTime(service.startTime)} -{' '}
                      {formatTime(service.endTime)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3499">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3500" />
                  <div data-id="element-3501">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3502">Locatie</p>
                    <p className="text-sm text-gray-600" data-id="element-3503">
                      {project?.location.address},{' '}
                      {project?.location.postalCode} {project?.location.city}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3504">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3505" />
                  <div data-id="element-3506">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3507">
                      Instructeur
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3508">{instructor?.name}</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3509">
                  <UsersIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3510" />
                  <div data-id="element-3511">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3512">
                      Deelnemers
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3513">
                      {participants.length} deelnemers
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3514">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3515" />
                  <div data-id="element-3516">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3517">Pauze</p>
                    <p className="text-sm text-gray-600" data-id="element-3518">
                      {service.breakDuration} minuten
                    </p>
                  </div>
                </div>
                {service.specialNotes && <div className="flex items-start" data-id="element-3519">
                    <AlertTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" data-id="element-3520" />
                    <div data-id="element-3521">
                      <p className="text-sm font-medium text-gray-900" data-id="element-3522">
                        Bijzonderheden
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-3523">
                        {service.specialNotes}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </Card>
          {/* Client details */}
          <Card data-id="element-3524">
            <div className="p-4 border-b border-gray-200" data-id="element-3525">
              <h3 className="font-medium text-gray-900" data-id="element-3526">Klant Informatie</h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-3527">
              <div className="space-y-3" data-id="element-3528">
                <div className="flex items-start" data-id="element-3529">
                  <UsersIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3530" />
                  <div data-id="element-3531">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3532">Klant</p>
                    <p className="text-sm text-gray-600" data-id="element-3533">
                      {project?.clientName}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3534">
                  <ClipboardIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3535" />
                  <div data-id="element-3536">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3537">Project</p>
                    <p className="text-sm text-gray-600" data-id="element-3538">
                      {project?.projectNumber}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3539">
                  <InfoIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3540" />
                  <div data-id="element-3541">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3542">
                      Kostenplaats
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3543">
                      {project?.costCenter}
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-3544">
                  <InfoIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3545" />
                  <div data-id="element-3546">
                    <p className="text-sm font-medium text-gray-900" data-id="element-3547">
                      Kostendrager
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-3548">
                      {project?.costBearer}
                    </p>
                  </div>
                </div>
                {project?.contactPerson && <div className="flex items-start" data-id="element-3549">
                    <UserIcon className="h-5 w-5 text-gray-400 mr-2 mt-0.5" data-id="element-3550" />
                    <div data-id="element-3551">
                      <p className="text-sm font-medium text-gray-900" data-id="element-3552">
                        Contactpersoon
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-3553">
                        {project.contactPerson}
                      </p>
                      {project.contactEmail && <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-3554">
                          <MailIcon className="h-3 w-3 mr-1" data-id="element-3555" />
                          <a href={`mailto:${project.contactEmail}`} className="text-[#B61C1C] hover:underline" data-id="element-3556">
                            {project.contactEmail}
                          </a>
                        </div>}
                      {project.contactPhone && <div className="flex items-center mt-1 text-xs text-gray-600" data-id="element-3557">
                          <PhoneIcon className="h-3 w-3 mr-1" data-id="element-3558" />
                          <a href={`tel:${project.contactPhone}`} className="text-[#B61C1C] hover:underline" data-id="element-3559">
                            {project.contactPhone}
                          </a>
                        </div>}
                    </div>
                  </div>}
              </div>
            </div>
          </Card>
        </div>
        {/* Actions card */}
        <Card data-id="element-3560">
          <div className="p-4 border-b border-gray-200" data-id="element-3561">
            <h3 className="font-medium text-gray-900" data-id="element-3562">Acties</h3>
          </div>
          <div className="p-4" data-id="element-3563">
            <div className="flex flex-wrap gap-3" data-id="element-3564">
              {role === 'instructor' && service.status === ServiceStatus.CONFIRMED && <Button variant="primary" data-id="element-3565">
                    <CheckIcon className="h-4 w-4 mr-1" data-id="element-3566" />
                    Start Training
                  </Button>}
              {role === 'instructor' && service.status === ServiceStatus.IN_PROGRESS && <Button variant="primary" data-id="element-3567">
                    <CheckIcon className="h-4 w-4 mr-1" data-id="element-3568" />
                    Rond Training Af
                  </Button>}
              {(role === 'planner' || role === 'manager') && <Button variant="outline" data-id="element-3569">
                  <EditIcon className="h-4 w-4 mr-1" data-id="element-3570" />
                  Bewerken
                </Button>}
              <Button variant="outline" data-id="element-3571">
                <DownloadIcon className="h-4 w-4 mr-1" data-id="element-3572" />
                Documenten
              </Button>
              <Button variant="outline" data-id="element-3573">
                <PrinterIcon className="h-4 w-4 mr-1" data-id="element-3574" />
                Afdrukken
              </Button>
            </div>
          </div>
        </Card>
      </div>;
  };
  // Render participants tab
  const renderParticipantsTab = () => {
    return <Card data-id="element-3575">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-3576">
          <h3 className="font-medium text-gray-900" data-id="element-3577">Deelnemers</h3>
          <div className="flex items-center" data-id="element-3578">
            <span className="text-sm text-gray-500 mr-2" data-id="element-3579">
              Aanwezigheid bijgewerkt:
            </span>
            {attendanceChecked ? <Badge variant="success" data-id="element-3580">Ja</Badge> : <Badge variant="warning" data-id="element-3581">Nee</Badge>}
          </div>
        </div>
        <div className="p-4" data-id="element-3582">
          <div className="overflow-x-auto" data-id="element-3583">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3584">
              <thead className="bg-gray-50" data-id="element-3585">
                <tr data-id="element-3586">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3587">
                    Naam
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3588">
                    E-mail
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3589">
                    Bedrijf
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3590">
                    Aanwezig
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3591">
                    Notities
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3592">
                {participants.map(participant => <tr key={participant.id} data-id="element-3593">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3594">
                      <div className="text-sm font-medium text-gray-900" data-id="element-3595">
                        {participant.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3596">
                      <div className="text-sm text-gray-600" data-id="element-3597">
                        {participant.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3598">
                      <div className="text-sm text-gray-600" data-id="element-3599">
                        {participant.company}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3600">
                      {participant.present === true && <Badge variant="success" data-id="element-3601">Aanwezig</Badge>}
                      {participant.present === false && <Badge variant="danger" data-id="element-3602">Afwezig</Badge>}
                      {participant.present === null && <div className="flex space-x-1" data-id="element-3603">
                          <button className="p-1 rounded bg-green-100 text-green-800 hover:bg-green-200" onClick={() => {
                      // Mark as present
                      setAttendanceChecked(true);
                    }} data-id="element-3604">
                            <CheckIcon className="h-4 w-4" data-id="element-3605" />
                          </button>
                          <button className="p-1 rounded bg-red-100 text-red-800 hover:bg-red-200" onClick={() => {
                      // Mark as absent
                      setAttendanceChecked(true);
                    }} data-id="element-3606">
                            <XIcon className="h-4 w-4" data-id="element-3607" />
                          </button>
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3608">
                      {participant.notes ? <div className="text-sm text-gray-600" data-id="element-3609">
                          {participant.notes}
                        </div> : <input type="text" className="text-sm border border-gray-300 rounded px-2 py-1 w-full" placeholder="Notitie toevoegen..." data-id="element-3610" />}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex justify-end" data-id="element-3611">
            <Button variant="primary" data-id="element-3612">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-3613" />
              Aanwezigheid Opslaan
            </Button>
          </div>
        </div>
      </Card>;
  };
  // Render documents tab
  const renderDocumentsTab = () => {
    return <Card data-id="element-3614">
        <div className="p-4 border-b border-gray-200" data-id="element-3615">
          <h3 className="font-medium text-gray-900" data-id="element-3616">Documenten</h3>
        </div>
        <div className="p-4" data-id="element-3617">
          <div className="overflow-x-auto" data-id="element-3618">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-3619">
              <thead className="bg-gray-50" data-id="element-3620">
                <tr data-id="element-3621">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3622">
                    Naam
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3623">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3624">
                    Grootte
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3625">
                    Datum
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3626">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-3627">
                {documents.map(document => <tr key={document.id} className="hover:bg-gray-50" data-id="element-3628">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3629">
                      <div className="text-sm font-medium text-gray-900" data-id="element-3630">
                        {document.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3631">
                      <div className="text-sm text-gray-600" data-id="element-3632">
                        {document.type.toUpperCase()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3633">
                      <div className="text-sm text-gray-600" data-id="element-3634">
                        {document.size}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3635">
                      <div className="text-sm text-gray-600" data-id="element-3636">
                        {document.date.toLocaleDateString('nl-NL')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-3637">
                      <Button variant="ghost" size="sm" data-id="element-3638">
                        <DownloadIcon className="h-4 w-4 mr-1" data-id="element-3639" />
                        Download
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {role !== 'instructor' && <div className="mt-4 flex justify-end" data-id="element-3640">
              <Button variant="outline" data-id="element-3641">
                <FileTextIcon className="h-4 w-4 mr-1" data-id="element-3642" />
                Document Toevoegen
              </Button>
            </div>}
        </div>
      </Card>;
  };
  // Render notes tab
  const renderNotesTab = () => {
    return <Card data-id="element-3643">
        <div className="p-4 border-b border-gray-200" data-id="element-3644">
          <h3 className="font-medium text-gray-900" data-id="element-3645">Notities & Rapportage</h3>
        </div>
        <div className="p-4 space-y-4" data-id="element-3646">
          <div data-id="element-3647">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3648">
              Trainingsrapportage
            </label>
            <textarea rows={6} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" placeholder="Voer hier je trainingsrapportage in..." defaultValue={service.notes} data-id="element-3649"></textarea>
          </div>
          <div data-id="element-3650">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3651">
              Interne notities (alleen zichtbaar voor instructeurs en planners)
            </label>
            <textarea rows={4} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#B61C1C] focus:border-[#B61C1C] sm:text-sm" placeholder="Voer hier interne notities in..." data-id="element-3652"></textarea>
          </div>
          <div className="flex justify-end" data-id="element-3653">
            <Button variant="primary" data-id="element-3654">
              <CheckIcon className="h-4 w-4 mr-1" data-id="element-3655" />
              Notities Opslaan
            </Button>
          </div>
        </div>
      </Card>;
  };
  return <div className="space-y-6" data-id="element-3656">
      <div className="flex justify-between items-center" data-id="element-3657">
        <div data-id="element-3658">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-3659">
            {service.description}
          </h1>
          <div className="flex items-center mt-1 space-x-2" data-id="element-3660">
            <Badge variant="default" data-id="element-3661">{service.trainingType}</Badge>
            {getStatusBadge(service.status)}
            {service.requiresProtocol && <Badge variant="warning" data-id="element-3662">Protocol Vereist</Badge>}
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)} data-id="element-3663">
          Terug naar overzicht
        </Button>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-3664">
        <nav className="-mb-px flex space-x-8" data-id="element-3665">
          <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview' ? 'border-[#B61C1C] text-[#B61C1C]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('overview')} data-id="element-3666">
            Overzicht
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'participants' ? 'border-[#B61C1C] text-[#B61C1C]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('participants')} data-id="element-3667">
            Deelnemers
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'documents' ? 'border-[#B61C1C] text-[#B61C1C]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('documents')} data-id="element-3668">
            Documenten
          </button>
          <button className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'notes' ? 'border-[#B61C1C] text-[#B61C1C]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('notes')} data-id="element-3669">
            Notities
          </button>
        </nav>
      </div>
      {/* Tab content */}
      <div data-id="element-3670">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'participants' && renderParticipantsTab()}
        {activeTab === 'documents' && renderDocumentsTab()}
        {activeTab === 'notes' && renderNotesTab()}
      </div>
    </div>;
}